import { concatById, F, sortById, T } from '@/helpers'
import { type EpgInfo } from '@setplex/tria-api'
import { createEvent, createStore, sample } from 'effector'
import { type EpgRequestParams } from '../index.h'
import { narrowEpgRequestToNecessary } from '../lib/epg'
import { isValidRange } from '../lib/ranges'
import { fetchEpgInfo, fetchEpgInfoFx, generateEmptyProgramsFx } from './fetch'
import { $loadedEpgRanges } from './ranges'

// whole epg matrix (array of Maps), by epg id
export const $epg = createStore<Map<string, EpgInfo[]>[]>([new Map()])

// fetch epg data request
export const fetchEpg = createEvent<EpgRequestParams>()

// for requested range, calculate biggest common unloaded range for all channels
sample({
  clock: fetchEpg,
  source: $loadedEpgRanges,
  filter: (_, requested) => {
    return requested.channels.length > 0 && isValidRange(requested.period)
  },
  fn: ([loadedRanges], requested) => {
    return narrowEpgRequestToNecessary({ requested, loadedRanges })
  },
  target: fetchEpgInfo,
})

// fill epg matrix with loaded data or empty programs if there is no channelEpgIds,
// do not fill epg matrix with empty programs if fetchEpgInfoFx failed,
// because it will be filled later (when fetchEpgInfoFx with same channelEpgIds will be done)
sample({
  clock: [fetchEpgInfoFx.done, generateEmptyProgramsFx.done],
  source: $epg,
  fn: ([programs], { params: { epgIds }, result: { epg } }) => {
    for (const id of epgIds) {
      const loaded = programs.get(id)
      const received = epg[id]

      const merged = sortById(concatById(loaded)(received)) as EpgInfo[]
      programs.set(id, merged)
    }
    return [programs]
  },
  target: $epg,
})

export const $epgIsReady = createStore(true).on(fetchEpgInfoFx, F)

sample({
  clock: fetchEpgInfoFx.finally,
  fn: T,
  target: $epgIsReady,
})

// Used only in one place in player for UI ($currentProgram) after $epgIsReady has been updated
export const $epgsEffectIsReady = createStore(false)

sample({
  clock: [fetchEpgInfoFx.finally, generateEmptyProgramsFx.finally],
  fn: T,
  target: $epgsEffectIsReady,
})
